import { CSSProperties, FormEvent, ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import type { User } from '@supabase/supabase-js';
import { BrandGlyph, MODULE_COLOR, BRAND_VERSION } from '../lib/branding';
import { safeTopPad, safeBottomPad } from '../lib/safeArea';
import { useSession } from '../hooks/useSession';
import { useEntitlement } from '../hooks/useEntitlement';
import { supabase } from '../lib/supabase';
import { haptic } from '../lib/haptics';
import { usePreferences } from '../lib/preferences';
import { Switch } from '../components/Switch';
import { apiFetch, ApiError } from '../lib/apiClient';
import {
  openManageSubscriptions,
  purchase,
  restorePurchases,
  type IapPackage,
} from '../lib/iap';
import {
  useShouldAutoOpenPaywall,
  useClearPaywallDeeplinkOnMount,
} from '../components/RequirePro';
import {
  ChevronRightIcon,
  EyeIcon,
  EyeOffIcon,
} from '../components/UIIcons';

/* ============================================================
   AccountSettingsPage — Account, subscription, preferences.
   ------------------------------------------------------------
   The subscription card is wired to StoreKit via RevenueCat
   (see lib/iap.ts). "Manage subscription" routes to Apple's
   built-in subscription management UI; "Restore purchases"
   runs StoreKit's restore flow. Account deletion calls the
   backend's /account/delete endpoint synchronously so we can
   confirm to the user that their data is gone before signing
   them out.
   ============================================================ */

export default function AccountSettingsPage() {
  const nav = useNavigate();
  const { session, signOut } = useSession();
  const user = session?.user;
  const email = user?.email ?? '';
  const userId = user?.id ?? '';

  const handleSignOut = async () => {
    void haptic('tap');
    await signOut();
    nav('/app', { replace: true });
  };

  return (
    <main style={shellStyle}>
      <nav style={navStyle}>
        <Link to="/app" style={navBrandLinkStyle}>
          <BrandGlyph size={22} color={MODULE_COLOR.angle} />
          <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: -0.1 }}>
            CADtomie Intraop
          </span>
        </Link>
        <Link to="/app" style={navBackStyle}>← Back</Link>
      </nav>

      <article style={articleStyle}>
        <header style={pageHeaderStyle}>
          <h1 style={titleStyle}>Account</h1>
          <p style={subtitleStyle}>
            Your sign-in, subscription, and app preferences.
          </p>
        </header>

        <UserHeader user={user ?? null} email={email} />

        <SectionHeading label="Subscription" />
        <SubscriptionCard />

        <SectionHeading label="Security" />
        <Card>
          <Row>
            <RowLabel>Email</RowLabel>
            <RowValue mono>{email || 'Not signed in'}</RowValue>
          </Row>
          <ChangePasswordForm />
          <Divider />
          <p style={emailNoteStyle}>
            Need to change your email?{' '}
            <a href="mailto:cadtomie@gmail.com" style={inlineLinkStyle}>
              Contact support
            </a>
            .
          </p>
          <Divider />
          <button type="button" style={ghostBtnStyle} onClick={handleSignOut}>
            Sign out
          </button>
        </Card>

        <SectionHeading label="Preferences" />
        <PreferencesCard />

        <TrustNote />

        <SectionHeading label="Legal" />
        <LegalCard />

        <SectionHeading label="Danger zone" tone="danger" />
        <DangerCard email={email} userId={userId} onDeleted={async () => {
          await signOut();
          nav('/app', { replace: true });
        }} />

        <p style={versionStyle}>
          CADtomie Intraop · v{BRAND_VERSION} (1)
        </p>
      </article>
    </main>
  );
}

/* ─────────────────── User header ─────────────────── */

function UserHeader({ user, email }: { user: User | null; email: string }) {
  const { isPro } = useEntitlement();
  const meta = (user?.user_metadata ?? {}) as {
    full_name?: string;
  };
  const initials = computeInitials(email, meta.full_name);
  const displayName = meta.full_name?.trim() || email.split('@')[0];
  const plan = isPro ? 'Pro' : 'Educational';

  return (
    <section style={userHeaderStyle}>
      <div style={avatarStyle} aria-hidden>
        <span style={avatarTextStyle}>{initials}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        <div style={userNameStyle}>{displayName}</div>
        <div style={userEmailStyle}>{email}</div>
        <div style={planChipStyle}>{plan}</div>
      </div>
    </section>
  );
}

function computeInitials(email: string, fullName?: string): string {
  if (fullName) {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  const local = (email.split('@')[0] || '··').slice(0, 2);
  return local.toUpperCase();
}

/* ─────────────────── Subscription card ─────────────────── */

function SubscriptionCard() {
  const ent = useEntitlement();
  const { isPro, loading, refresh } = ent;
  const { expiresAt, willRenew, source } = ent.entitlement;
  // State-machine based UI selection.
  //
  // The four states the card distinguishes are:
  //   • `isWebPwa` — running in a normal browser, no native store
  //     available. Promote the iOS App Store as the path to Pro.
  //   • `isNativeError` — running natively (iOS/Android) but the
  //     RevenueCat SDK could not initialise (network, bad key, no
  //     Play Billing). Show a "service unavailable" recoverable
  //     state with a Retry affordance — never silently masquerade
  //     as Pro.
  //   • Pro state — render the management surface.
  //   • Non-Pro native state — render the upgrade CTA / paywall.
  //
  // Note: Android with a successfully-initialised RevenueCat falls
  // into the same "Pro vs Non-Pro native" branches as iOS — they
  // share the paywall, the purchase flow, and the manage path.
  const isWebPwa = source === 'web-free';
  const isNativeError = source === 'native-error';
  // Auto-open the paywall when the user was redirected here from a
  // RequirePro-gated route (URL hash `#paywall`). Reviewer-friendly
  // and matches the user expectation: "I tapped a module, show me
  // what to do next."
  const autoOpen = useShouldAutoOpenPaywall();
  // Suppress the auto-open paywall in states where it would dead-end
  // (web PWA shows a notice instead, native-error has nothing to show
  // until RevenueCat recovers).
  const [showPaywall, setShowPaywall] = useState(
    autoOpen && !isWebPwa && !isNativeError,
  );
  useClearPaywallDeeplinkOnMount(autoOpen);
  const [restoring, setRestoring] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const handleManage = async () => {
    void haptic('tap');
    await openManageSubscriptions();
  };

  const handleRestore = async () => {
    if (restoring) return;
    void haptic('tap');
    setRestoring(true);
    setMsg(null);
    try {
      const result = await restorePurchases();
      await refresh();
      setMsg(
        result.isPro
          ? 'Subscription restored.'
          : `No active subscription found on this ${getStoreCopy().account}.`,
      );
      void haptic(result.isPro ? 'success' : 'tap');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Restore failed.');
      void haptic('error');
    } finally {
      setRestoring(false);
    }
  };

  const planLabel = isNativeError
    ? 'Subscription service unavailable'
    : isWebPwa
      ? 'Web (free)'
      : isPro
        ? 'CADtomie Intraop Pro'
        : 'Educational (free)';
  const statusLabel = isNativeError
    ? 'Could not reach RevenueCat — retry below'
    : isWebPwa
      ? 'All measurement modules unlocked on the web'
      : isPro
        ? willRenew
          ? 'Active · auto-renew on'
          : 'Active · auto-renew off'
        : 'Free educational access';
  const renewsLabel = expiresAt
    ? `${willRenew ? 'Renews' : 'Ends'} ${expiresAt.toLocaleDateString()}`
    : '—';

  return (
    <Card>
      <Row>
        <RowLabel>Current plan</RowLabel>
        <RowValue>{loading ? '…' : planLabel}</RowValue>
      </Row>
      <Row>
        <RowLabel>Status</RowLabel>
        <RowValue dim>{statusLabel}</RowValue>
      </Row>
      <Row last>
        <RowLabel>{willRenew ? 'Renews' : 'Ends'}</RowLabel>
        <RowValue dim>{renewsLabel}</RowValue>
      </Row>

      <div style={{ display: 'flex', gap: 8, marginTop: 4, flexDirection: 'column' }}>
        {isNativeError ? (
          // Native shell failed to bring up RevenueCat. Offer a Retry
          // button — refresh() re-fetches CustomerInfo and re-runs
          // the offering load implicitly via the EntitlementProvider.
          <button
            type="button"
            style={primaryBtnStyle}
            onClick={() => {
              void haptic('tap');
              void refresh();
            }}
          >
            Retry
          </button>
        ) : isWebPwa ? (
          // Web users: no purchase / management surface, just an
          // App Store deep-link for users who want the native
          // iOS experience.
          <a
            href="https://apps.apple.com/app/cadtomie-intraop/id6770692246"
            target="_blank"
            rel="noopener noreferrer"
            style={primaryBtnStyle}
          >
            Get the iOS app
          </a>
        ) : isPro ? (
          <button type="button" style={primaryBtnStyle} onClick={handleManage}>
            Manage subscription
          </button>
        ) : (
          <button
            type="button"
            style={primaryBtnStyle}
            onClick={() => {
              void haptic('tap');
              setShowPaywall(true);
            }}
          >
            Upgrade to Pro
          </button>
        )}
        {/* "View all plans" + "Restore purchases" only render in
            true store-backed states. They have nothing to do on the
            web PWA or in the native-error fallback. */}
        {!isWebPwa && !isNativeError ? (
          <>
            <button
              type="button"
              style={ghostBtnStyle}
              onClick={() => {
                void haptic('tap');
                setShowPaywall(true);
              }}
            >
              View all plans
            </button>
            <button
              type="button"
              style={ghostBtnStyle}
              onClick={handleRestore}
              disabled={restoring}
            >
              {restoring ? 'Restoring…' : 'Restore purchases'}
            </button>
          </>
        ) : null}
      </div>

      {msg ? <p style={statusOkStyle}>{msg}</p> : null}

      <p style={smallNoteStyle}>
        {isNativeError
          ? 'We could not reach the subscription service. This is usually transient — tap Retry above, check your network, and try again. If the problem persists, contact cadtomie@gmail.com.'
          : isWebPwa
            ? 'The web version of CADtomie Intraop is free for educational use. For the full intraoperative experience with offline capture and haptics, install the iOS app.'
            : 'Educational access stays free for non-commercial learning and demonstration. Pro unlocks the full intraoperative measurement workflow. Subscriptions auto-renew through your account until cancelled in the platform settings (iOS Settings → Apple ID → Subscriptions, or Play Store → Profile → Subscriptions).'}
      </p>

      {showPaywall ? (
        <Paywall
          onClose={() => setShowPaywall(false)}
          onPurchased={async () => {
            await refresh();
            setShowPaywall(false);
            setMsg('Welcome to Pro.');
            void haptic('success');
          }}
        />
      ) : null}
    </Card>
  );
}

/* ─────────────────── Paywall ─────────────────── */

/* Platform-aware copy for subscription-management language. RevenueCat
 * abstracts the purchase flow but the disclosure UI has to reflect
 * which store is actually handling the money — Apple uses "Apple ID"
 * and "iOS Settings", Google uses "Google account" and "Play Store".
 * Web falls back to neutral copy. */
function getStoreCopy(): {
  account: string;
  settingsPath: string;
  cancelHint: string;
} {
  const platform = Capacitor.getPlatform();
  if (platform === 'android') {
    return {
      account: 'Google account',
      settingsPath: 'Play Store → Profile → Payments & subscriptions → Subscriptions',
      cancelHint: 'Cancel anytime in the Play Store.',
    };
  }
  if (platform === 'ios') {
    return {
      account: 'Apple ID',
      settingsPath: 'iOS Settings → Apple ID → Subscriptions',
      cancelHint: 'Cancel anytime in iOS Settings.',
    };
  }
  return {
    account: 'store account',
    settingsPath: 'your store settings',
    cancelHint: 'Cancel anytime from your store settings.',
  };
}

function Paywall({
  onClose,
  onPurchased,
}: {
  onClose: () => void;
  onPurchased: () => Promise<void> | void;
}) {
  // Packages come from the global context — they were pre-fetched at
  // app start, so opening the paywall is instant.
  const { packages, packagesLoading, refreshPackages } = useEntitlement();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const storeCopy = getStoreCopy();
  // The WebPaywallNotice (App-Store-deeplink + contact line) is for
  // the browser PWA only. On any native shell — iOS Capacitor as
  // well as Android Capacitor — RevenueCat is wired up, the
  // offering loads, and the regular package list rendered below
  // should be shown. Previously this check also fired on Android
  // (`getPlatform() !== 'ios'`), which intercepted real Android
  // purchases and showed the iOS deeplink instead.
  const isWeb = !Capacitor.isNativePlatform();

  async function handleBuy(pkg: IapPackage) {
    if (busy) return;
    void haptic('tap');
    setBusy(true);
    setErr(null);
    const result = await purchase(pkg.identifier);
    setBusy(false);

    if (result.kind === 'success') {
      void haptic('success');
      await onPurchased();
      return;
    }
    if (result.kind === 'cancelled') {
      // Silent — user dismissed the StoreKit sheet on purpose.
      return;
    }
    setErr(result.message || 'Purchase failed.');
    void haptic('error');
  }

  return (
    <div style={paywallBackdropStyle} role="dialog" aria-modal="true">
      <div style={paywallSheetStyle}>
        <h3 style={paywallTitleStyle}>CADtomie Intraop Pro</h3>
        <p style={paywallSubtitleStyle}>
          Full access to angle, distance, height, and torsion measurement
          modules. {storeCopy.cancelHint}
        </p>

        {isWeb ? (
          <WebPaywallNotice />
        ) : packagesLoading && packages.length === 0 ? (
          <p style={paywallNoteStyle}>Loading plans…</p>
        ) : packages.length === 0 ? (
          <div>
            <p style={paywallNoteStyle}>
              No subscription plans available right now. Make sure you're
              signed in with your {storeCopy.account} and try again.
            </p>
            <button
              type="button"
              style={ghostBtnStyle}
              onClick={() => void refreshPackages()}
            >
              Reload plans
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
            {packages
              .slice()
              .sort((a) => (a.period === 'annual' ? -1 : 1))
              .map((pkg) => (
                <button
                  key={pkg.identifier}
                  type="button"
                  onClick={() => handleBuy(pkg)}
                  disabled={busy}
                  style={paywallPlanBtnStyle}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span style={{ fontWeight: 600 }}>
                      {pkg.period === 'annual' ? 'Annual' : 'Monthly'}
                    </span>
                    {pkg.pricePerMonth && pkg.period === 'annual' ? (
                      <span style={{ fontSize: 12, opacity: 0.7 }}>
                        {pkg.pricePerMonth} per month
                      </span>
                    ) : null}
                  </span>
                  <span style={{ fontWeight: 600 }}>{pkg.priceString}</span>
                </button>
              ))}
          </div>
        )}

        {err ? <p style={statusErrStyle}>{err}</p> : null}

        <p style={paywallFineStyle}>
          Payment is charged to your {storeCopy.account}. Subscription
          auto-renews unless cancelled at least 24h before the end of the
          period. Manage or cancel anytime in {storeCopy.settingsPath}. See
          our{' '}
          <Link to="/terms" style={inlineLinkStyle}>Terms</Link> and{' '}
          <Link to="/privacy" style={inlineLinkStyle}>Privacy Policy</Link>.
        </p>

        <button type="button" style={ghostBtnStyle} onClick={onClose} disabled={busy}>
          Close
        </button>
      </div>
    </div>
  );
}

/* ─────────────────── Web paywall notice ───────────────────
   Shown when the paywall is opened in a browser instead of
   the iOS app. The user can't subscribe here — Apple's
   StoreKit only exists inside the native app — but the
   default "Loading…" / "No plans available" copy is confusing
   for a fresh visitor. We replace it with an honest
   explanation plus an App Store deep link. */

function WebPaywallNotice() {
  // App Store URL — once published this should point to the real
  // App Store listing. While in review we deep-link to the search
  // page so an iPhone user lands somewhere sensible. Update the
  // URL after the first approved release.
  const APP_STORE_URL =
    'https://apps.apple.com/app/cadtomie-intraop/id6770692246';

  return (
    <div style={webNoticeStyle}>
      <p style={webNoticeTitleStyle}>
        Pro subscriptions live in the iOS app.
      </p>
      <p style={paywallNoteStyle}>
        For technical reasons, paid plans are only available inside
        the CADtomie Intraop iOS app — the web version stays free
        for educational access.
      </p>

      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={primaryBtnStyle}
      >
        Open in the App Store
      </a>

      <p style={paywallFineStyle}>
        On a desktop or Android? Drop us a line at{' '}
        <a
          href="mailto:cadtomie@gmail.com?subject=Pro%20access%20request"
          style={inlineLinkStyle}
        >
          cadtomie@gmail.com
        </a>{' '}
        and we'll set you up directly.
      </p>
    </div>
  );
}

/* ─────────────────── Change password ─────────────────── */

function ChangePasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setMsg(null);

    if (next.length < 6) {
      setMsg({ kind: 'err', text: 'New password must be at least 6 characters.' });
      return;
    }
    if (next !== confirm) {
      setMsg({ kind: 'err', text: "New passwords don't match." });
      return;
    }
    if (!current) {
      setMsg({ kind: 'err', text: 'Enter your current password to confirm.' });
      return;
    }

    setBusy(true);
    try {
      const sessionData = await supabase.auth.getSession();
      const email = sessionData.data.session?.user.email;
      if (!email) throw new Error('Session lost — please sign in again.');

      const { error: verifyErr } = await supabase.auth.signInWithPassword({
        email,
        password: current,
      });
      if (verifyErr) {
        throw new Error('Current password is incorrect.');
      }

      const { error } = await supabase.auth.updateUser({ password: next });
      if (error) throw error;

      setCurrent('');
      setNext('');
      setConfirm('');
      setMsg({ kind: 'ok', text: 'Password updated.' });
      void haptic('success');
    } catch (err) {
      setMsg({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Update failed.',
      });
      void haptic('error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={subFormStyle} noValidate>
      <div style={subFormTitleStyle}>Change password</div>
      <PasswordInput
        value={current}
        onChange={setCurrent}
        placeholder="Current password"
        autoComplete="current-password"
      />
      <PasswordInput
        value={next}
        onChange={setNext}
        placeholder="New password"
        autoComplete="new-password"
      />
      <PasswordInput
        value={confirm}
        onChange={setConfirm}
        placeholder="Confirm new password"
        autoComplete="new-password"
      />
      {msg && (
        <p
          role={msg.kind === 'err' ? 'alert' : 'status'}
          style={msg.kind === 'ok' ? statusOkStyle : statusErrStyle}
        >
          {msg.text}
        </p>
      )}
      <button
        type="submit"
        disabled={busy || !current || !next || !confirm}
        style={primaryBtnStyle}
      >
        {busy ? 'Updating…' : 'Update password'}
      </button>
    </form>
  );
}

function PasswordInput({
  value,
  onChange,
  placeholder,
  autoComplete,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  autoComplete: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div style={passwordWrapStyle}>
      <input
        type={show ? 'text' : 'password'}
        autoComplete={autoComplete}
        placeholder={placeholder}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, paddingRight: 44 }}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        aria-label={show ? 'Hide password' : 'Show password'}
        style={eyeBtnStyle}
        tabIndex={-1}
      >
        {show ? <EyeOffIcon width={18} height={18} /> : <EyeIcon width={18} height={18} />}
      </button>
    </div>
  );
}

/* ─────────────────── Preferences ─────────────────── */

function PreferencesCard() {
  const prefs = usePreferences();

  return (
    <Card>
      <Row last>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <RowLabel>Haptic feedback</RowLabel>
          <span style={subRowHintStyle}>
            Tap-back when a capture or export lands.
          </span>
        </div>
        <Switch
          checked={prefs.hapticsEnabled}
          onChange={(v) => {
            void haptic('tap');
            prefs.setHapticsEnabled(v);
          }}
          label="Haptic feedback"
        />
      </Row>
      <p style={smallNoteStyle}>
        More preferences (units, language, export format) will appear here as
        the app grows.
      </p>
    </Card>
  );
}

/* ─────────────────── Trust note ─────────────────── */

function TrustNote() {
  return (
    <p style={trustNoteStyle}>
      Your measurements stay on this device. Nothing is uploaded.
    </p>
  );
}

/* ─────────────────── Legal ─────────────────── */

function LegalCard() {
  return (
    <Card padded={false}>
      <LegalRow to="/privacy">Privacy Policy</LegalRow>
      <LegalRow to="/terms">Terms of Use</LegalRow>
      <LegalRow to="/impressum" last>Impressum</LegalRow>
    </Card>
  );
}

function LegalRow({
  to,
  children,
  last,
}: {
  to: string;
  children: ReactNode;
  last?: boolean;
}) {
  return (
    <Link
      to={to}
      style={{
        ...legalRowStyle,
        borderBottom: last ? 'none' : '1px solid rgba(255,255,255,0.05)',
      }}
    >
      <span style={legalRowTextStyle}>{children}</span>
      <ChevronRightIcon
        width={16}
        height={16}
        style={{ color: 'var(--text-muted)' }}
      />
    </Link>
  );
}

/* ─────────────────── Danger zone ─────────────────── */

function DangerCard({
  email,
  userId,
  onDeleted,
}: {
  email: string;
  userId: string;
  onDeleted: () => Promise<void> | void;
}) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function handleClick() {
    if (busy) return;

    const confirmed = window.confirm(
      'Delete your CADtomie account?\n\n' +
        '• Your sign-in and Supabase user record will be removed immediately.\n' +
        "• Locally stored content (annotated images, captured frames) lives only on this device — uninstall the app to remove it.\n" +
        "• Active store subscriptions are NOT cancelled by account deletion — cancel them separately in your store settings (iOS Settings → Apple ID → Subscriptions, or Play Store → Profile → Payments & subscriptions).\n\n" +
        'This cannot be undone.',
    );
    if (!confirmed) return;

    if (!userId) {
      setStatus({ kind: 'err', text: 'You need to be signed in to delete your account.' });
      return;
    }

    setBusy(true);
    setStatus(null);
    try {
      await apiFetch('/account/delete', { method: 'POST' });
      void haptic('warning');
      setStatus({ kind: 'ok', text: 'Account deleted. Signing you out…' });
      await onDeleted();
    } catch (err) {
      void haptic('error');
      // Verbose diagnostics — we route account-deletion failures through
      // a Sentry-style message so iOS-side issues (CORS, DNS, 401, 5xx)
      // surface to the user instead of a generic "Load failed". The
      // ApiError class exposes status + body; everything else (network,
      // TLS, blocked) lands in the generic Error branch.
      let detail = 'Unknown error';
      if (err instanceof ApiError) {
        detail = `HTTP ${err.status}${err.body ? ` — ${err.body.slice(0, 120)}` : ''}`;
      } else if (err instanceof Error) {
        detail = `${err.name}: ${err.message}`;
      }
      console.error('[account-delete] failed:', detail, err);
      setStatus({
        kind: 'err',
        text: `Deletion failed (${detail}). Email cadtomie@gmail.com if this persists.`,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <p style={sectionBodyStyle}>
        Permanently deletes your CADtomie sign-in and Supabase user record
        ({email || 'not signed in'}). Locally stored content lives only on
        this device — uninstall the app to remove it.
      </p>
      <button
        type="button"
        style={dangerBtnStyle}
        onClick={handleClick}
        disabled={busy || !userId}
      >
        {busy ? 'Deleting…' : 'Delete account'}
      </button>
      {status ? (
        <p
          role={status.kind === 'err' ? 'alert' : 'status'}
          style={status.kind === 'ok' ? statusOkStyle : statusErrStyle}
        >
          {status.text}
        </p>
      ) : null}
    </Card>
  );
}

/* ─────────────────── Layout primitives ─────────────────── */

function SectionHeading({
  label,
  tone = 'default',
}: {
  label: string;
  tone?: 'default' | 'danger';
}) {
  return (
    <h2
      style={{
        ...sectionHeadingStyle,
        color: tone === 'danger' ? 'rgba(255, 107, 107, 0.85)' : 'var(--text-muted)',
      }}
    >
      {label}
    </h2>
  );
}

function Card({
  children,
  padded = true,
}: {
  children: ReactNode;
  padded?: boolean;
}) {
  return (
    <section style={{ ...cardStyle, padding: padded ? '16px 18px' : 0 }}>
      {children}
    </section>
  );
}

function Row({ children, last }: { children: ReactNode; last?: boolean }) {
  return (
    <div
      style={{
        ...rowStyle,
        borderBottom: last ? 'none' : '1px solid rgba(255,255,255,0.05)',
      }}
    >
      {children}
    </div>
  );
}

function RowLabel({ children }: { children: ReactNode }) {
  return <span style={rowLabelStyle}>{children}</span>;
}

function RowValue({
  children,
  mono,
  dim,
}: {
  children: ReactNode;
  mono?: boolean;
  dim?: boolean;
}) {
  return (
    <span
      style={{
        ...rowValueStyle,
        ...(mono ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 } : null),
        ...(dim ? { color: 'var(--text-muted)' } : null),
      }}
    >
      {children}
    </span>
  );
}

function Divider() {
  return <div style={dividerStyle} />;
}

/* ═════════════════════════════ STYLES ═════════════════════════════ */

const shellStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100dvh',
  background: '#000',
};

const navStyle: CSSProperties = {
  maxWidth: 600,
  width: '100%',
  margin: '0 auto',
  paddingTop: safeTopPad(14),
  paddingBottom: 14,
  paddingLeft: 18,
  paddingRight: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const navBrandLinkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  color: 'var(--text)',
  textDecoration: 'none',
};

const navBackStyle: CSSProperties = {
  fontSize: 14,
  color: 'var(--text-dim)',
  textDecoration: 'none',
  padding: '6px 4px',
};

const articleStyle: CSSProperties = {
  maxWidth: 600,
  width: '100%',
  margin: '0 auto',
  padding: `4px 18px ${safeBottomPad(48)}`,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
};

const pageHeaderStyle: CSSProperties = {
  marginBottom: 22,
};

const titleStyle: CSSProperties = {
  margin: '8px 0 4px',
  fontSize: 34,
  fontWeight: 700,
  letterSpacing: -1.0,
  color: 'var(--text)',
};

const subtitleStyle: CSSProperties = {
  margin: 0,
  color: 'var(--text-dim)',
  fontSize: 14,
  lineHeight: 1.5,
};

const userHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '14px 16px',
  marginBottom: 24,
  background:
    'linear-gradient(180deg, rgba(124,196,255,0.06) 0%, rgba(124,196,255,0.02) 100%)',
  border: '1px solid rgba(124,196,255,0.14)',
  borderRadius: 18,
};

const avatarStyle: CSSProperties = {
  flexShrink: 0,
  width: 52,
  height: 52,
  borderRadius: '50%',
  background:
    'linear-gradient(135deg, rgba(124,196,255,0.32) 0%, rgba(124,196,255,0.12) 100%)',
  border: '1px solid rgba(124,196,255,0.28)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 14px rgba(124, 196, 255, 0.12)',
};

const avatarTextStyle: CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  color: "#95a2c8",
  letterSpacing: 0.5,
};

const userNameStyle: CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text)',
  letterSpacing: -0.2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const userEmailStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--text-dim)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const planChipStyle: CSSProperties = {
  marginTop: 4,
  alignSelf: 'flex-start',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: "#95a2c8",
  background: 'rgba(124,196,255,0.10)',
  border: '1px solid rgba(124,196,255,0.22)',
  padding: '2px 8px',
  borderRadius: 999,
};

const sectionHeadingStyle: CSSProperties = {
  margin: '8px 4px 8px',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1.1,
  textTransform: 'uppercase',
};

const cardStyle: CSSProperties = {
  background: 'rgba(20, 20, 24, 0.7)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  boxShadow:
    '0 1px 0 rgba(255,255,255,0.03) inset, 0 4px 16px rgba(0,0,0,0.35)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  marginBottom: 18,
  overflow: 'hidden',
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  paddingTop: 8,
  paddingBottom: 8,
  minHeight: 28,
};

const rowLabelStyle: CSSProperties = {
  color: 'var(--text-dim)',
  fontSize: 14,
  flexShrink: 0,
};

const rowValueStyle: CSSProperties = {
  color: 'var(--text)',
  fontSize: 14,
  textAlign: 'right',
  wordBreak: 'break-all',
};

const subRowHintStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--text-muted)',
  lineHeight: 1.4,
  maxWidth: 240,
};

const dividerStyle: CSSProperties = {
  height: 1,
  background: 'rgba(255,255,255,0.05)',
  margin: '4px 0',
};

const primaryBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  background: 'rgba(124,196,255,0.14)',
  color: "#95a2c8",
  border: '1px solid rgba(124,196,255,0.30)',
  borderRadius: 12,
  padding: '12px 16px',
  fontSize: 15,
  fontWeight: 600,
  textDecoration: 'none',
  cursor: 'pointer',
  font: 'inherit',
  transition: 'background-color 160ms ease, transform 120ms ease',
  WebkitTapHighlightColor: 'transparent',
};

const ghostBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  color: 'var(--text-dim)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 12,
  padding: '11px 16px',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  font: 'inherit',
  transition: 'background-color 160ms ease, border-color 160ms ease',
  WebkitTapHighlightColor: 'transparent',
};

const dangerBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  color: '#ff8a8a',
  border: '1px solid rgba(255, 138, 138, 0.30)',
  borderRadius: 12,
  padding: '11px 16px',
  fontSize: 14,
  fontWeight: 500,
  textDecoration: 'none',
  cursor: 'pointer',
  font: 'inherit',
  transition: 'background-color 160ms ease, border-color 160ms ease',
  WebkitTapHighlightColor: 'transparent',
};

const subFormStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  paddingTop: 4,
  paddingBottom: 4,
};

const subFormTitleStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  letterSpacing: -0.1,
  marginBottom: 2,
};

const inputStyle: CSSProperties = {
  width: '100%',
  background: 'rgba(0,0,0,0.35)',
  border: '1px solid var(--hairline)',
  borderRadius: 12,
  color: 'var(--text)',
  padding: '12px 14px',
  fontSize: 16,
  lineHeight: 1.3,
  outline: 'none',
  WebkitTapHighlightColor: 'transparent',
  font: 'inherit',
  boxSizing: 'border-box',
  transition: 'border-color 160ms ease, background-color 160ms ease',
};

const passwordWrapStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

const eyeBtnStyle: CSSProperties = {
  position: 'absolute',
  right: 4,
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'transparent',
  border: 'none',
  color: 'var(--text-muted)',
  padding: 10,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  WebkitTapHighlightColor: 'transparent',
};

const statusOkStyle: CSSProperties = {
  margin: 0,
  color: 'var(--good)',
  fontSize: 13,
  lineHeight: 1.45,
};

const statusErrStyle: CSSProperties = {
  margin: 0,
  color: 'var(--danger)',
  fontSize: 13,
  lineHeight: 1.45,
};

const sectionBodyStyle: CSSProperties = {
  margin: 0,
  color: 'var(--text-dim)',
  fontSize: 13,
  lineHeight: 1.55,
};

const trustNoteStyle: CSSProperties = {
  margin: '8px 4px 22px',
  fontSize: 12,
  color: 'var(--text-muted)',
  textAlign: 'center',
  lineHeight: 1.5,
};

const smallNoteStyle: CSSProperties = {
  margin: 0,
  fontSize: 12,
  color: 'var(--text-muted)',
  lineHeight: 1.5,
};

const legalRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 18px',
  color: 'var(--text)',
  textDecoration: 'none',
  fontSize: 15,
  transition: 'background-color 120ms ease',
  WebkitTapHighlightColor: 'transparent',
};

const legalRowTextStyle: CSSProperties = {
  color: 'var(--text)',
  fontSize: 15,
};

const versionStyle: CSSProperties = {
  margin: '14px 0 0',
  fontSize: 11,
  color: 'var(--text-muted)',
  textAlign: 'center',
  letterSpacing: 0.3,
  fontVariantNumeric: 'tabular-nums',
};

const emailNoteStyle: CSSProperties = {
  margin: 0,
  padding: '4px 0',
  fontSize: 13,
  color: 'var(--text-dim)',
  lineHeight: 1.55,
};

const inlineLinkStyle: CSSProperties = {
  color: "#95a2c8",
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  WebkitTapHighlightColor: 'transparent',
};

/* ── Paywall sheet ───────────────────────────── */
const paywallBackdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.72)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  zIndex: 1000,
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
};

const paywallSheetStyle: CSSProperties = {
  width: '100%',
  maxWidth: 480,
  background: '#0a0a0c',
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  border: '1px solid rgba(255,255,255,0.08)',
  padding: `22px 20px ${safeBottomPad(20)}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const paywallTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: -0.4,
  color: 'var(--text)',
};

const paywallSubtitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: 'var(--text-dim)',
  lineHeight: 1.5,
};

const paywallNoteStyle: CSSProperties = {
  margin: '8px 0',
  fontSize: 13,
  color: 'var(--text-muted)',
  lineHeight: 1.5,
};

const paywallPlanBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  background: 'rgba(124,196,255,0.10)',
  color: "#95a2c8",
  border: '1px solid rgba(124,196,255,0.28)',
  borderRadius: 14,
  padding: '14px 16px',
  fontSize: 15,
  cursor: 'pointer',
  font: 'inherit',
  textAlign: 'left',
  WebkitTapHighlightColor: 'transparent',
};

const paywallFineStyle: CSSProperties = {
  margin: '6px 0 4px',
  fontSize: 11,
  color: 'var(--text-muted)',
  lineHeight: 1.5,
};

/* Web-only paywall empty state. Same visual rhythm as the
   regular paywall but uses warm amber accent so it reads as
   informational rather than as a failure state. */
const webNoticeStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '14px 16px',
  marginTop: 4,
  background: 'rgba(255,159,10,0.08)',
  border: '1px solid rgba(255,159,10,0.22)',
  borderRadius: 14,
};

const webNoticeTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text)',
  letterSpacing: -0.1,
};
